import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import evidenceDiary from '../../../../../api/diaries/evidenceDiary/hooks'
import { EvidenceDiary } from '../../../../../api/diaries/evidenceDiary/types'
import { triggerDownload } from '../../../../../api/globalFunctions'
import { APPLICATION_SETTINGS, MODAL_TYPES } from '../../../../../common/constants'
import { setDeleteCallback } from '../../../../../Components/Modals/DeleteModal/utils'
import { PrintableColumnDef } from '../../../../../Components/TanstackTable'
import { showModal } from '../../../../../store/modal'
import { formatNumberWithTwoDecimals } from '../../../../../utils/general'
import { renderAreaUnit, renderCurrency, renderCurrencyWithUnit } from '../../../../../utils/property'
import columnWidths from './columnWidths'

export const useTableColumns = () => {
    const dispatch = useDispatch()
    const { t } = useTranslation()

    const currentLang = localStorage.getItem('i18nextLng') || 'hr'
    const w = useMemo(() => ({ ...columnWidths.hr, ...(columnWidths[currentLang] ?? {}) }), [currentLang])

    const areaUnit = APPLICATION_SETTINGS.AREA_UNIT
    const currency = APPLICATION_SETTINGS.CURRENCY

    const { mutate: deleteRecord } = evidenceDiary.DeleteDiary.useMutation()
    const { mutate: downloadPdf } = evidenceDiary.DownloadDiaryPdf.useMutation({
        onSuccess: (data, id) => {
            const blob = new Blob([Uint8Array.from(atob(data), (c) => c.charCodeAt(0))], { type: 'application/pdf' })
            triggerDownload(id, blob, `Evidencijski-list-${id}.pdf`)
        },
    })

    const handleDelete = useCallback(
        (id: number) => {
            const callbackId = 'delete-evidence-diary'
            setDeleteCallback(callbackId, () => deleteRecord(id))
            dispatch(showModal({ modalType: MODAL_TYPES.DELETE_MODAL, modalProps: { callbackId } }))
        },
        [dispatch, deleteRecord]
    )

    return useMemo(
        (): PrintableColumnDef<EvidenceDiary>[] => [
            {
                header: t('app.common.propertyId'),
                id: 'internalAppPropertyId',
                accessorKey: 'internalAppPropertyId',
                enableSorting: true,
                size: w.internalAppPropertyId,
            },
            {
                header: t('table.header.diary.sellerName'),
                id: 'sellerFullName',
                accessorFn: (row) => row.evidenceDiarySeller?.map((s) => s.fullName).join(', ') ?? null,
                cell: ({ row }) => row.original.evidenceDiarySeller?.map((s) => <div key={s.id ?? s.fullName}>{s.fullName}</div>),
                printValue: (row) => row.evidenceDiarySeller?.map((s) => s.fullName).join(', ') ?? '',
                enableSorting: true,
                size: w.sellerName,
            },
            {
                header: t('app.common.pin'),
                id: 'evidenceDiarySellerPin',
                accessorFn: (row) => row.evidenceDiarySeller?.map((s) => s.taxNumber).join(', ') ?? null,
                cell: ({ row }) => row.original.evidenceDiarySeller?.map((s) => <div key={s.id ?? s.taxNumber}>{s.taxNumber}</div>),
                printValue: (row) => row.evidenceDiarySeller?.map((s) => s.taxNumber).join(', ') ?? '',
                enableSorting: false,
                size: w.sellerOib,
            },
            {
                header: t('table.header.diary.contractType'),
                id: 'contractType',
                accessorKey: 'contractType',
                enableSorting: true,
                size: w.contractType,
            },
            {
                header: t('table.header.diary.propertyType'),
                id: 'propertyType',
                accessorKey: 'propertyType',
                enableSorting: true,
                size: w.propertyType,
            },
            {
                header: t('app.common.location'),
                id: 'propertyLocation',
                accessorKey: 'propertyLocation',
                enableSorting: true,
                size: w.propertyLocation,
            },
            {
                header: t('app.common.street'),
                id: 'propertyStreet',
                accessorKey: 'propertyStreet',
                enableSorting: true,
                size: w.propertyStreet,
            },
            {
                header: t('app.common.area', { areaUnit: renderAreaUnit(areaUnit) }),
                id: 'area',
                enableSorting: true,
                size: w.area,
                cell: ({ row }) => {
                    const area = Number(row.original.area)
                    return <>{area ? formatNumberWithTwoDecimals(area) : null}</>
                },
                printValue: (row) => {
                    const area = Number(row.area)
                    return area ? formatNumberWithTwoDecimals(area) : ''
                },
            },
            {
                header: t('table.header.diary.price', { price: renderCurrency(currency) }),
                id: 'price',
                enableSorting: true,
                size: w.price,
                cell: ({ row }) => {
                    const price = Number(row.original.price)
                    return <>{price ? formatNumberWithTwoDecimals(price) : null}</>
                },
                printValue: (row) => {
                    const price = Number(row.price)
                    return price ? formatNumberWithTwoDecimals(price) : ''
                },
            },
            {
                header: t('table.header.diary.priceM2', { priceM2: renderCurrencyWithUnit(currency, areaUnit) }),
                id: 'priceM2',
                enableSorting: false,
                size: w.priceM2,
                cell: ({ row }) => {
                    const priceM2 = Number(row.original.priceM2)
                    return <>{priceM2 ? formatNumberWithTwoDecimals(priceM2) : null}</>
                },
                printValue: (row) => {
                    const priceM2 = Number(row.priceM2)
                    return priceM2 ? formatNumberWithTwoDecimals(priceM2) : ''
                },
            },
            {
                header: t('app.common.agencyCommission'),
                id: 'agencyCommission',
                accessorKey: 'agencyCommission',
                enableSorting: false,
                size: w.agencyCommission,
            },
            {
                header: t('app.common.contractDate'),
                id: 'contractDate',
                accessorKey: 'contractDate',
                enableSorting: true,
                size: w.contractDate,
            },
            {
                id: 'actions',
                enableResizing: false,
                size: w.actions,
                meta: { thDivClass: 'text-center', stopPropagation: true },
                cell: ({ row }) => {
                    const id = row.original.id
                    return (
                        <ul className="list-inline hstack gap-2 mb-0 justify-content-center">
                            <li className="list-inline-item">
                                <Link to="#" title={t('button.download')} className="text-warning d-inline-block" onClick={() => downloadPdf(id)}>
                                    <i className="mdi mdi-download fs-16"></i>
                                </Link>
                            </li>
                            <li className="list-inline-item edit">
                                <Link title={t('button.edit')} to={`/evidence-diary/${id}/edit`} className="text-info d-inline-block edit-item-btn">
                                    <i className="mdi mdi-pencil-outline fs-16"></i>
                                </Link>
                            </li>
                            <li className="list-inline-item">
                                <Link
                                    to="#"
                                    title={t('button.delete')}
                                    className="text-danger d-inline-block remove-item-btn"
                                    onClick={() => handleDelete(id)}
                                >
                                    <i className="mdi mdi-trash-can-outline fs-16"></i>
                                </Link>
                            </li>
                        </ul>
                    )
                },
            },
        ],
        [w, areaUnit, currency, t, downloadPdf, handleDelete]
    )
}
